import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {DecimalPipe} from '@angular/common';
import {environment} from '../../../../../../environments/environment';
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs/Subject";
import {AppState, OrderCreationState, selectDefaultPaymentData, selectFooterAction, selectOrderCreation, selectUser} from '@store/reducers';
import {Store} from "@ngrx/store";
import {AdyenService} from "@services/adyen.service";
import {User} from "@models/user.interface";
import {FooterActionEnum} from "@models/footer-action.enum";
import {LoadUserCards} from "@store/actions/profile.action";
import {Subscription} from "rxjs/Subscription";
import {Shipment} from '@models/shipment.interface';
import 'rxjs/add/operator/map';

@Component({
  selector: 'app-payment-method',
  templateUrl: './payment-method-form.component.html',
  styleUrls: ['./payment-method-form.component.scss']
})

export class PaymentMethodFormComponent implements OnInit {
  @Input() billingAddress: any;
  @Input() disabledInvoiceMethod: boolean;
  private shipments: Shipment[];
  submitted: boolean = false;
  user: User;

  defaultPaymentData: any;

  @ViewChild('formData') public formData: ElementRef;

  public cardNumMask = [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/];
  public expDateMask = [/\d/, /\d/, '/', /\d/, /\d/];
  public cvcMask = [/\d/, /\d/, /\d/];
  public form: FormGroup;
  private unsubscribe: Subject<void> = new Subject();
  adyenSubscription: Subscription;
  constructor(
    private store: Store<AppState>,
    private adyenService: AdyenService,
    private dp: DecimalPipe
  ) {}

  @Output() handleOrderCreation: EventEmitter<any> = new EventEmitter<any>();

  ngOnInit() {
    this.store.select(selectOrderCreation)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((state: OrderCreationState) => {
        this.shipments = Object.keys(state.shipments).map(id => {
          return {id, ...state.shipments[id]};
        });
      });
    this.form = this.generateForm();
    this.form.get('card.payment_card_expiry_date').valueChanges.subscribe(data => {
      const [expiry_month, expiry_year] = data.split('/');
      this.form.get('card').patchValue({expiry_month, expiry_year: '20' + expiry_year});
    });

    this.form.get('payment_service').valueChanges.subscribe(data => {
      if (data === '1') {
        this.form.removeControl('invoice');
        this.form.removeControl('card');
        this.form.addControl('card', this.generateCardPaymentService());
        this.form.get('card.payment_card_expiry_date').valueChanges.subscribe(data => {
          const [expiry_month, expiry_year] = data.split('/');
          this.form.get('card').patchValue({expiry_month, expiry_year: '20' + expiry_year});
        });
        if (!this.adyenSubscription) {
          this.adyenSubscription = this.adyenService.initAdyen()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe(this.adyenService.handleSubscribe(this.formData, this.form.get('card')));
        }
      }
      if (data === '2') {
        this.form.addControl('invoice', this.generateInvoicePaymentService());
        this.form.removeControl('card');
      }
    });
    this.store.select(selectFooterAction)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(action => {
        if (action === FooterActionEnum.GO_TO_CREATE_CONSIGNMENT_ORDER_COMPLETE) {
          this.submitted = true;
          if (this.form.valid) {
            this.handleOrderCreation.emit(this.form.value);
          }
        }
      });

    this.store.select(selectUser)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((user: User) => {
        this.user = user;
      });
    this.store.dispatch(new LoadUserCards());
    this.store.select(selectDefaultPaymentData)
      .subscribe(data => {
        this.defaultPaymentData = data;
        this.form.get('payment_service').setValue(String(this.defaultPaymentData.method));
      });
  }

  generateForm() {
    return new FormGroup({
      payment_service: this.generateFormControl('1'),
      card: this.generateCardPaymentService(),

    });
  }

  generateCardPaymentService() {
    const card = this.defaultPaymentData && this.defaultPaymentData.card;

    const holderName = card && card.holder_name || environment.Payment.holderName;
    const expiryDate = card && this.generateExpiryDate(card.expiry_month, card.expiry_year) || environment.Payment.expiryDate;
    const expiryMonth = card && card.expiry_month || environment.Payment.expiryMonth;
    const expiryYear = card && card.expiry_year || environment.Payment.expiryYear;
    return new FormGroup({
      payment_card: this.generateFormControl(
        environment.Payment.cardNumber, [PaymentMethodFormComponent.creditCardNumberSupportedValidator]
      ),
      payment_card_holder_name: this.generateFormControl(holderName,[Validators.minLength(2)]),
      payment_card_expiry_date: this.generateFormControl(expiryDate),
      expiry_month:  this.generateFormControl(expiryMonth),
      expiry_year: this.generateFormControl(expiryYear),
      payment_cvc: this.generateFormControl(environment.Payment.cvc, [Validators.minLength(3)]),
      save_card_information: new FormControl(false),
      save_default_card: new FormControl(false),
    });
  }

  generateExpiryDate(month, year) {
    return `${this.dp.transform(month, '2.0-0')}/${year.toString().slice(-2)}`;
  }

  generateInvoicePaymentService() {
    if (this.user.is_company) {
      return new FormGroup({
        is_payer_company: this.generateFormControl(this.user.is_company),
        organisation: new FormControl(this.user.company_name),
        identity_number: new FormControl(this.user.register_num),
        order_reference: new FormControl(this.getReference())
        // order_reference: this.generateFormControl(`${this.user.name} ${this.user.surname}`),
      });

    } else {
      return new FormGroup({
        is_payer_company: this.generateFormControl(this.user.is_company),
        name: new FormControl(this.user.name),
        surname: new FormControl(this.user.surname),
        identity_number: this.generateFormControl('', [Validators.pattern('^[1-9][0-9]*$')]),
      });
    }

  }

  getReference() {
    if (this.shipments.length === 1) {
      return this.shipments[0].details.reference;
    }

    return '';
  }

  generateFormControl(initialValue, validators = []) {
    return new FormControl(initialValue, [Validators.required, ...validators]);
  }

  static creditCardNumberSupportedValidator(control) {
    if (control.value.replace( /\D/g, '' ).match(/(^(?:5[1-5][0-9]{2}|222[1-9]|22[3-9][0-9]|2[3-6][0-9]{2}|27[01][0-9]|2720)[0-9]{12}$)|(^4[0-9]{12}(?:[0-9]{3})?$)/g)) {
      return null;
    } else {
      return { 'numberNotSupportedCreditCard': true };
    }
  }
  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}



// WEBPACK FOOTER //
// ./src/app/modules/consignment/order-summary/components/payment-method-form/payment-method-form.component.ts